import { create } from 'zustand';

/**
 * 토스트 종류.
 * - `success` : 민트 아이콘. 코드 등록 성공 등 긍정 상황.
 * - `error`   : 빨간 아이콘. 검증 실패 등 부정 상황.
 * - `info`    : 파란 아이콘. 중복/안내성 메시지.
 */
export type ToastVariant = 'success' | 'error' | 'info';

export interface Toast {
  /** 클라이언트에서 발급한 고유 ID. */
  id: string;
  /** 토스트 시각 종류. */
  variant: ToastVariant;
  /** 본문 메시지(한국어). */
  message: string;
}

/**
 * 토스트 옵션 — `id`는 자동 발급되므로 외부에서는 입력할 필요가 없다.
 */
export type ToastInput = Omit<Toast, 'id'>;

interface ToastState {
  toasts: Toast[];
  push: (input: ToastInput) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

/**
 * 페이지 어디서나 토스트를 띄울 수 있는 전역 스토어(Zustand).
 *
 * 사용 예:
 *   const push = useToastStore((s) => s.push);
 *   push({ variant: 'success', message: '코드 등록 완료!' });
 *
 * `<ToastProvider />`가 마운트되면 4초 후 자동으로 dismiss한다.
 */
export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push: (input) => {
    const id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    set((state) => ({ toasts: [...state.toasts, { ...input, id }] }));
    return id;
  },
  dismiss: (id) =>
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
  clear: () => set({ toasts: [] }),
}));
